import {
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { finalize, map, startWith, switchMap } from 'rxjs/operators';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';

import {
  QaiButtonComponent,
  QaiFieldComponent,
  QaiFloatingSelectComponent,
  QaiIconComponent,
  QaiLoadingComponent,
  QaiSelectComponent,
} from '@qai/ui';

import { ScreenShareSectionComponent } from 'src/app/pages/self-serve/simulation-builder/simulation-details/general-details/screen-share-section/screen-share-section.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IAvatarReplica } from 'src/app/interfaces/self-serve/responses/iavatarReplicas';
import { VideoOption } from 'src/app/interfaces/self-serve/ivideo-option';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';

// interface VoiceOption {
//   value: string;
//   label: string;
// }

export interface AvatarSelection {
  replicaId: string;
  gender?: string;
}

const DEFAULT_AVATAR_PLACEHOLDER = '/assets/img/simulation-images/simulation-avatar.svg';

@Component({
  selector: 'avatar-look-voice-selector',
  standalone: true,
  templateUrl: './avatar-look-voice-selector.component.html',
  styleUrls: ['./avatar-look-voice-selector.component.scss'],
  imports: [
    FormsModule,
    TranslocoModule,
    QaiFieldComponent,
    QaiFloatingSelectComponent,
    QaiSelectComponent,
    QaiButtonComponent,
    QaiIconComponent,
    QaiLoadingComponent,
    ScreenShareSectionComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AvatarLookVoiceSelectorComponent implements OnInit, OnChanges {
  private readonly authorService = inject(AuthorService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly cdRef = inject(ChangeDetectorRef);
  // private readonly translocoService = inject(TranslocoService);

  readonly form = input<FormGroup>();
  readonly disabled = input(false);
  readonly savingPersona = input(false);
  readonly simulationUuid = input('');
  readonly moduleUuid = input('');
  /** Transloco key for the appearance field label. */
  readonly appearanceLabel = input('simulationBuilder.AVATAR_LOOK_LABEL');
  readonly hideScreenshare = input(false);
  readonly isSelfAuthoring = input(false);
  readonly conversePromptManualOverride = input(false);

  readonly avatarChange = output<AvatarSelection | undefined>();
  /** Emits the form's already-saved avatar once replicas load (no user selection involved). */
  readonly savedAvatarResolved = output<AvatarSelection>();

  // voiceOptions: VoiceOption[] = [];
  readonly defaultPlaceholder = DEFAULT_AVATAR_PLACEHOLDER;

  readonly avatarOptions = signal<VideoOption[]>([]);
  readonly selectedAvatar = signal<string | undefined>(undefined);
  // readonly selectedVoice = signal<string | undefined>(undefined);
  readonly isLoadingAvatars = signal(false);

  private screenshareFeatureEnabled: boolean = false;
  private replicaGenders = new Map<string, string>();

  get showScreenshareSection(): boolean {
    return this.screenshareFeatureEnabled && !this.hideScreenshare();
  }

  readonly personaId = toSignal(
    toObservable(this.form).pipe(
      switchMap((form) => {
        const personaIdControl = form?.get('personaId');
        return personaIdControl
          ? personaIdControl.valueChanges.pipe(startWith(personaIdControl.value))
          : of('');
      }),
      map((personaId) => personaId ?? '')
    ),
    { initialValue: '' }
  );

  readonly previewImageUrl = computed(
    () => this.avatarOptions().find((option) => option.value === this.selectedAvatar())?.photoUrl
  );

  ngOnInit(): void {
    this.selectedAvatar.set(this.form()?.get('replicaId')?.value);
    this.loadAvatarReplicas();
    this.resolveScreenshareVisibility();
    // this.loadVoiceOptions();
  }

  private async resolveScreenshareVisibility(): Promise<void> {
    this.screenshareFeatureEnabled = await this.featureFlagsService.isFlagEnabled(AppConstants.SCREENSHARE);
    // the await resolves after the initial change detection pass
    this.cdRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['form'] && this.form() && this.selectedAvatar() === undefined) {
      this.selectedAvatar.set(this.form()?.get('replicaId')?.value);
    }
  }

  onAvatarSelectionChange(option: VideoOption | undefined): void {
    this.selectedAvatar.set(option?.value);
    this.patchFormAvatar(option?.value);
    this.avatarChange.emit(
      option ? { replicaId: option.value, gender: this.replicaGenders.get(option.value) } : undefined
    );
  }

  // onVoiceSelectionChange(value: string): void {
  //   this.selectedVoice.set(value);
  // }

  resetSelection(): void {
    this.selectedAvatar.set(undefined);
    // this.selectedVoice.set(undefined);
  }

  // onPlayVoicePreview(): void {
  //   // static UI only — no audio wired up yet
  // }

  private loadAvatarReplicas(): void {
    this.isLoadingAvatars.set(true);
    this.authorService
      .getAvatarReplicas()
      .pipe(finalize(() => this.isLoadingAvatars.set(false)))
      .subscribe({
        next: (replicas: IAvatarReplica[]) => {
          this.replicaGenders = new Map(
            replicas
              .filter((replica) => !!replica.gender)
              .map((replica) => [replica.replica_id, replica.gender as string])
          );
          this.avatarOptions.set(
            replicas.map((replica) => ({
              value: replica.replica_id,
              label: replica.replica_name,
              photoUrl: replica.thumbnail_s3_url,
              videoUrl: replica.thumbnail_video_url,
            }))
          );
          this.emitSavedAvatar();
        },
        error: (err) => console.error('Failed to load avatar replicas', err),
      });
  }

  private emitSavedAvatar(): void {
    const replicaId = this.form()?.get('replicaId')?.value;
    if (replicaId && this.avatarOptions().some((option) => option.value === replicaId)) {
      this.savedAvatarResolved.emit({ replicaId, gender: this.replicaGenders.get(replicaId) });
    }
  }

  private patchFormAvatar(value: string | undefined): void {
    this.form()?.get('replicaId')?.patchValue(value, { emitEvent: false });
  }

  // private loadVoiceOptions(): void {
  //   this.translocoService
  //     .selectTranslation('simulationBuilder')
  //     .pipe(take(1))
  //     .subscribe(() => {
  //       this.voiceOptions = [
  //         { value: 'voice-1', label: this.translocoService.translate('simulationBuilder.VOICE_OPTION_1') },
  //         { value: 'voice-2', label: this.translocoService.translate('simulationBuilder.VOICE_OPTION_2') },
  //       ];
  //     });
  // }
}
